import {isPlainObject, noop as _noop} from 'lodash';
import {
  Expression,
  type ExpressionElement,
  EVariable,
  ERecord,
  ECall,
  EFunction,
  EUnion,
  ECase,
  EList,
  EElement,
  EExtend,
  ESelect,
  ETyped,
  EDistinct,
} from '@packages/systems/wfdl-ast';
import {updateAt} from '@packages/utilities/array';
import * as Variable from '../Variable';
import * as VariableMap from '../VariableMap';
import * as EvalEnv from '../EvalEnv';
import * as Result from '../Result';
import type {DisplayName} from '../DisplayName';
// !Important! Do NOT change these imports to the root Component or Plugins folders.
// That will cause a circular dependency that breaks the build and nothing will work.
import {type ComponentName, name as componentName} from '../Component/Names';
import {name as pluginName} from '../Plugins/Names';
import {matchExpression} from '../matchExpression';
import {ExpressionPathPart, type ExpressionPath} from '../ExpressionPath';
import {
  type Qualified,
  type Identifier,
  namesEqual,
  toHumanReadableString,
  getName,
  getPluginName,
  qualify,
  identifier,
  TypeName,
} from '@packages/systems/wfdl-Qualified';
import {
  constantFalse,
  emptyObject,
  identity,
  emptyArray,
  constant,
  concat,
  constantTrue,
} from '@packages/utilities/fp/utils';
import {
  dissoc,
  map as mapValues,
  union as unionMerge,
} from '@packages/utilities/key-map';
import {type LiteralExpressionType} from '../GenericExpression/GenericExpressionTypes';
import {
  type Lens,
  Stoppable,
  _resume,
  _stop,
  _everything,
  _everythingWithContext,
  _everywhere,
  _everywhereStoppable,
  _everywhereWithContext,
  _everywhereWithState,
  _getIn,
  _getElementData,
  _setElementData,
  _updateIn,
  _setIn,
  _deconstructCall,
  _deconstructFunc,
  _pathsEqual,
  _isText,
  _isNumber,
  _isRecord,
  _getRecord,
  _getElement,
  _getText,
  _getTyped,
  _getBoolean,
  _getNumber,
  _isList,
  _isVariable,
  _isBinding,
  _isHole,
  _isCall,
  _isEnum,
  _getEnum,
  _getSelect,
  _getExtend,
  _getUnion,
  _getCall,
  _getCase,
  _getFunction,
  _getHole,
  _getList,
  _getLiteral,
  _getDistinct,
  _getVariable,
  _getElementId,
  _isDataVariable,
  _getTypedType,
  _isElement,
  _isFunction,
  _isSelect,
} from '../GenericExpression/GenericExpressionUtils';
import {NodePojo} from '@packages/systems/core/nodepojo';
import {reportBug} from '@packages/utilities/reporter';
import {Json} from '@packages/utilities/json';
import {getFirst, getSecond, pair, type Pair} from '@packages/utilities/Pair';
import {weakMemo} from '@packages/utilities/memo';

const ExpressionLens: Lens<Expression, Expression> = {
  get: identity,
  set: constant,
};

export const everywhere = _everywhere<Expression>(identity);
export const everywhereStoppable = _everywhereStoppable<Expression>(identity);
export const everywhereWithContext =
  _everywhereWithContext<Expression>(identity);
export const everywhereWithState =
  _everywhereWithState<Expression>(ExpressionLens);
export const everything = _everything<Expression>(identity);
export const everythingWithContext =
  _everythingWithContext<Expression>(identity);

export const getIn = _getIn<Expression>(identity);
export const setIn = _setIn<Expression>(identity);

export const stop = _stop;
export const resume = _resume;

/**
 * Updates an expression at the specified path using the provided function.
 */
export const updateIn = _updateIn<Expression>(identity);
export const getElementData = _getElementData<Expression>;
export const setElementData = _setElementData<Expression, Expression>;

/**
 * Splits an expression viewed as 0 or more nested Calls into an expression
 * representing the function and an array of expressions representing
 * the arguments.
 *
 * It helps to easily get at the function or arguments of an expression.
 */
export const deconstructCall = _deconstructCall<Expression>(identity);
export const deconstructFunc = _deconstructFunc<Expression>(identity);

export const pathsEqual = _pathsEqual;
export const isText = _isText<Expression>;
export const isNumber = _isNumber<Expression>;
export const isList = _isList<Expression>;
export const isVariable = _isVariable<Expression>;
export const isBinding = _isBinding<Expression>;
export const isDataVariable = _isDataVariable<Expression>;
export const isElement = _isElement<Expression>;
export const isFunction = _isFunction<Expression>;
export const isRecord = _isRecord<Expression>;
export const isHole = _isHole<Expression>;
export const isEnum = _isEnum<Expression>;
export const isCall = _isCall<Expression>;
export const isSelect = _isSelect<Expression>;

export const getRecord = _getRecord<Expression>;
export const getElement = _getElement<Expression>;
export const getText = _getText<Expression>;
export const getTyped = _getTyped<Expression>;
export const getBoolean = _getBoolean<Expression>;
export const getNumber = _getNumber<Expression>;
export const getEnum = _getEnum<Expression>;
export const getSelect = _getSelect<Expression>;
export const getExtend = _getExtend<Expression>;
export const getUnion = _getUnion<Expression>;
export const getCase = _getCase<Expression>;
export const getList = _getList<Expression>;
export const getVariable = _getVariable<Expression>;
export const getCall = _getCall<Expression>;
export const getFunction = _getFunction<Expression>;
export const getLiteral = _getLiteral<Expression>;
export const getDistinct = _getDistinct<Expression>;
export const getHole = _getHole<Expression>;
export const getElementId = _getElementId<Expression>;
export const getTypedType = _getTypedType<Expression>;

export const toHoleyExpression: (arg1: Expression) => Expression = identity;

export const isHoleExpression = matchExpression({
  Hole: constantTrue,
  _: constantFalse,
});

const eitherOrTrue =
  (x: boolean) =>
  (y: boolean): boolean =>
    x || y;

const SOME_FOUND = stop(true);
const SOME_NOT_FOUND_YET = resume(false);

/**
 * `some` calls a predicate function on all points of a given expression
 * and returns a boolean value saying whether the predicate passed on any of
 * them
 *
 * Use this for better memoization when `predicate` remains the same between
 * calls.
 *
 * ```
 * const containsOverrideBinding = some(isDataVariable);
 * ```
 */
export function some(
  predicate: (e: Expression) => boolean
): (e: Expression) => boolean {
  return everything(eitherOrTrue)((expr) =>
    predicate(expr) ? SOME_FOUND : SOME_NOT_FOUND_YET
  );
}

const containsHole = some(isHoleExpression);

export const fromHoleyExpression = (holey: Expression): null | Expression => {
  if (containsHole(holey)) {
    return null;
  }
  return holey;
};

/**
 *  Constructs a qualified Variable
 */
export const qualifiedVariable = (name: string, ident: string): Expression =>
  EVariable(Variable.qualified(qualify(pluginName(name), identifier(ident))));

type LitTypeMap = {
  Boolean: boolean;
  Number: number;
  Text: string;
  Enum: string;
  Literal: {name: Qualified<TypeName>; value: Json};
};

/**
 * Typesafe mechanism for retrieving literal values from element data.
 * An expression path is used in combination with a `LiteralExpressionType`
 * to extract the value of the literal, if the literal cannot be found in
 * the expression, the fallback value of the same type is returned.
 *
 * ```javascript
 * const tag = getElementDataAt(
 *   expr,
 *   [{in: 'Record', at: 'attr'}, {in: 'Record', at: 'my-attr'}],
 *   'Text',
 *   'default attr value'
 * );
 * ```
 *
 * Retrieves the string `val` in the `Text` expression found in the
 * `Element`'s `data.attr.my-attr` field. If such an expression pattern does not exist,
 * `'default attr value'` is returned.
 * ```
 */
export function getElementDataAt<T, K extends keyof LitTypeMap>(
  element: ExpressionElement,
  path: ExpressionPath,
  literal: K,
  fallback: T | LitTypeMap[K]
): T | LitTypeMap[K] {
  const literalExpression = getIn(getElementData(element), path);
  if (!literalExpression) return fallback;
  return getElementDataAtHandler(literalExpression)(literal, fallback);
}

// This util is very weakly typed. `any` here is essentially `T` from
// the caller above
const getElementDataAtHandler = matchExpression({
  Boolean: (val) => (literal: LiteralExpressionType, fallback: any) =>
    literal === 'Boolean' ? val : fallback,
  Number: (val) => (literal: LiteralExpressionType, fallback: any) =>
    literal === 'Number' ? val : fallback,
  Text: (val) => (literal: LiteralExpressionType, fallback: any) =>
    literal === 'Text' ? val : fallback,
  Enum: (val) => (literal: LiteralExpressionType, fallback: any) =>
    literal === 'Enum' ? val : fallback,
  Literal: (val) => (literal: LiteralExpressionType, fallback: any) =>
    literal === 'Literal' ? val : fallback,
  _: constant((_: string, fallback: any) => fallback),
});

const keepInnerElement =
  (
    outerEl: null | {
      data: Expression;
      id: string;
      type: Qualified<ComponentName>;
    }
  ) =>
  (
    innerEl: null | {
      data: Expression;
      id: string;
      type: Qualified<ComponentName>;
    }
  ) =>
    innerEl || outerEl;

/**
 * Returns the closest ancestral element that matches the `predicate` starting at the element of `id`.
 * If the element of `id` matches the predicate it will returned.
 */
export function closestElement(
  expression: Expression,
  id: string,
  predicate: (arg1: ExpressionElement) => boolean
): ExpressionElement | null {
  return everything(keepInnerElement)((expr) => {
    const isElementInSubtree = allElements(expr)[id];
    if (!isElementInSubtree) return stop(null);
    return resume(
      matchExpression<ExpressionElement | null>({
        Element: (el) => (predicate(el) ? el : null),
        _: returnNull,
      })(expr)
    );
  })(expression);
}

/**
 * Returns the direct parent of the element of `id`.
 */
export function getParent(
  expression: Expression,
  id: string
): ExpressionElement | null {
  function isNotElement(element: ExpressionElement) {
    return element.id !== id;
  }
  return closestElement(expression, id, isNotElement);
}

export function getStringFromDisplayName(
  displayName: DisplayName,
  options?: {
    element?: ExpressionElement;
    parentExpression?: Expression;
  }
): string {
  const element = options ? options.element : undefined;
  const parentExpression = options ? options.parentExpression : undefined;
  if (typeof displayName === 'string') {
    return displayName;
  } else if (displayName.type === 'Enumerated') {
    if (parentExpression && element) {
      const index = elementIndex(element.id)(parentExpression);
      if (index != null && index >= 0) {
        return `${displayName.value} ${index + 1}`;
      }
    }
    return displayName.value;
  } else if (displayName.type === 'Derived') {
    if (element) {
      return parentExpression
        ? displayName.getFromElement(element, parentExpression)
        : displayName.getFromElement(element);
    }
    return displayName.defaultValue;
  } else {
    /*:: (displayName: empty); */
    return 'Unknown';
  }
}

type ElementMap = Record<string, ExpressionElement>;
const returnNull = (_: Expression) => null;
const returnUndefined = (_: Expression) => undefined;
const returnEmptyArray = (_: Expression) => emptyArray;

/**
 * Takes an expression and returns a map of elements keyed by their `id`. This
 * is a memoized function and serves as a shared cache for many WFDL utilities.
 *
 * _Example_
 *
 * ```jsx
 * const elementsInComponent = allElements(component.render);
 * const isEveryElementBlock = Object.keys(elementsInComponent).every((key) => {
 *   return namesEqual(elementsInComponent[key].type, qualify([pluginName('Basic'), componentName('Block')]))
 * })
 * ```
 */
const noop = constant(_noop);
const walk = matchExpression<(cache: ElementMap) => void>({
  Element: (el) => (cache) => {
    cache[el.id] = el;
    walk(el.data)(cache);
  },
  Record: (record) => (cache) => {
    for (const key in record) {
      walk(record[key]!)(cache);
    }
  },
  List: (list) => (cache) => {
    const n = list.length;
    if (n === 0) return;
    for (let i = 0; i < n; i++) {
      walk(list[i]!)(cache);
    }
  },
  Function:
    ({body}) =>
    (cache: ElementMap) => {
      walk(body)(cache);
    },
  Call:
    ({fun, arg}) =>
    (cache: ElementMap) => {
      walk(fun)(cache);
      walk(arg)(cache);
    },
  Union:
    ({value}) =>
    (cache: ElementMap) => {
      walk(value)(cache);
    },
  Case:
    ({value, cases}) =>
    (cache: ElementMap) => {
      walk(value)(cache);
      for (const key in cases) {
        walk(cases[key]!)(cache);
      }
    },
  Distinct:
    ({value}) =>
    (cache) => {
      walk(value)(cache);
    },
  Extend:
    ({record, value}) =>
    (cache) => {
      walk(record)(cache);
      walk(value)(cache);
    },
  Typed:
    ({expr}) =>
    (cache) => {
      walk(expr)(cache);
    },
  Select:
    ({from}: {from: Expression}) =>
    (cache) => {
      walk(from)(cache);
    },
  _: constant(noop),
});

export const allElements = weakMemo((root: Expression): ElementMap => {
  const cache: ElementMap = {};
  walk(root)(cache);
  return cache;
});

/**
 * Given an Expression and an id, find an ElementExpression within with the
 * given id

 * _Example_
 *
 * ```jsx
 * const element = getElementById(component.render, 'some-id');
 * ```
 */
export function getElementById(
  expr: Expression,
  id: string
): ExpressionElement | null {
  const element = allElements(expr)[id];
  if (!element) return null;
  return element;
}

const combineNullable =
  <T>(x: T) =>
  (y: T): T =>
    x === null ? y : x;

// Single-pass expression updater that short-circuits once a target is found.
// Uses a mutable flag to avoid intermediary result objects.
//
// The `fn` callback MUST set `flagRef.found = true` when it locates the target
// expression. Once set, sibling subtrees are skipped and only ancestor nodes
// along the path to the target are rebuilt, preserving referential equality
// elsewhere.
const mut_updateOneExpr =
  (flagRef: {found: boolean}) => (fn: (expr: Expression) => Expression) => {
    const match: (expr: Expression) => Expression = matchExpression({
      Element: (el, expr) => {
        const newData = go(el.data);
        if (flagRef.found)
          return el.data === newData ? expr : EElement({...el, data: newData});
        return expr;
      },
      Record: (props, expr) => {
        for (const key in props) {
          const val = props[key]!;
          const newVal = go(val);
          if (flagRef.found)
            return val === newVal ? expr : ERecord({...props, [key]: newVal});
        }
        return expr;
      },
      List: (items, expr) => {
        for (let i = 0; i < items.length; i++) {
          const item = items[i]!;
          const newItem = go(item);
          if (flagRef.found) {
            if (item === newItem) return expr;
            const nextItems = items.slice();
            nextItems[i] = newItem;
            return EList(nextItems);
          }
        }
        return expr;
      },
      Function: ({body, param}, expr) => {
        const newBody = go(body);
        if (flagRef.found)
          return body === newBody ? expr : EFunction({body: newBody, param});
        return expr;
      },
      Call: ({fun, arg}, expr) => {
        const newFun = go(fun);
        if (flagRef.found)
          return fun === newFun ? expr : ECall({fun: newFun, arg});
        const newArg = go(arg);
        if (flagRef.found)
          return arg === newArg ? expr : ECall({fun, arg: newArg});
        return expr;
      },
      Union: ({tag, value}, expr) => {
        const newValue = go(value);
        if (flagRef.found)
          return value === newValue ? expr : EUnion({tag, value: newValue});
        return expr;
      },
      Case: ({value, cases}, expr) => {
        const newValue = go(value);
        if (flagRef.found)
          return value === newValue ? expr : ECase({value: newValue, cases});
        for (const key in cases) {
          const caseVal = cases[key]!;
          const newCase = go(caseVal);
          if (flagRef.found)
            return caseVal === newCase
              ? expr
              : ECase({value, cases: {...cases, [key]: newCase}});
        }
        return expr;
      },
      Extend: ({record, prop, value}, expr) => {
        const newRecord = go(record);
        if (flagRef.found)
          return record === newRecord
            ? expr
            : EExtend({record: newRecord, prop, value});
        const newValue = go(value);
        if (flagRef.found)
          return value === newValue
            ? expr
            : EExtend({record, prop, value: newValue});
        return expr;
      },
      Typed: ({type, expr: innerExpr}, expr) => {
        const newInner = go(innerExpr);
        if (flagRef.found)
          return innerExpr === newInner ? expr : ETyped({type, expr: newInner});
        return expr;
      },
      Distinct: ({name, value}, expr) => {
        const newValue = go(value);
        if (flagRef.found)
          return value === newValue ? expr : EDistinct({name, value: newValue});
        return expr;
      },
      Select: ({from, prop}, expr) => {
        const newFrom = go(from);
        if (flagRef.found)
          return from === newFrom ? expr : ESelect({from: newFrom, prop});
        return expr;
      },
      Boolean: (_val, expr) => expr,
      Number: (_val, expr) => expr,
      Text: (_val, expr) => expr,
      Enum: (_val, expr) => expr,
      Variable: (_val, expr) => expr,
      Literal: (_val, expr) => expr,
      Hole: (_val, expr) => expr,
    });

    const go = (expr: Expression): Expression => {
      const newExpr = fn(expr);
      return flagRef.found ? newExpr : match(expr);
    };
    return go;
  };

/**
 * Given an element ID and an expression, returns the index of the element with
 * that ID among its siblings or null if the element is not found.
 */
export const elementIndex = (
  id: string
): ((arg1: Expression) => null | number) =>
  everything(combineNullable)((expr) => {
    // Stop walking if el is not inside expr.
    if (!allElements(expr)[id]) {
      return stop(null);
    }
    return matchExpression<Stoppable<null | number>>({
      List: (list) => {
        const index = list.findIndex((item: Expression) =>
          matchExpression({
            Element: (el) => el.id === id,
            _: constantFalse,
          })(item)
        );
        if (index === -1) {
          return resume(null);
        }
        return stop(index);
      },
      _: (_) => resume(null),
    })(expr);
  });

/**
 * Takes an element's id, an updater function and an expression.
 * It calls the updater function if the element is found in the provided
 * expression and returns the updated function.
 *
 * If you're updating an element's data it is recommened that you also
 * use `getElementData` and `setElementData` to property support
 * `Typed` element data which is leveraged by user-land components.
 *
 * _Example_

 * ```jsx
 * const updatedElement = updateElement(
 *   elementId,
 *   el => ({
 *     ...el,
 *     data: EText('New text on paragraph'),
 *   }),
 *   expr
 * )
 * ```
 */
export const updateElement = (
  id: string,
  fn: (arg1: ExpressionElement) => ExpressionElement,
  expr: Expression
) => {
  // Early exit optimization: use memoized getElementById (O(1) lookup on warm cache
  // via allElements) to check if the element exists and if updating it would be a no-op.
  const element = getElementById(expr, id);
  if (!element) {
    return expr;
  }

  const updatedElement = fn(element);
  if (updatedElement === element) {
    return expr;
  }

  const flagRef = {found: false};
  return mut_updateOneExpr(flagRef)((e) => {
    const el = getElement(e);
    if (el && el.id === id) {
      flagRef.found = true;
      return EElement(updatedElement);
    }
    return e;
  })(expr);
};

/**
 * Get an array of all the variable names encountered in an expression.
 */
const variableNamesMatcher = matchExpression({
  Function: (fn) => [fn.param],
  _: returnEmptyArray,
});

export const variableNames = everything<Array<Identifier>>(concat)((expr) =>
  resume(variableNamesMatcher(expr))
);

type FreeVariableBindings = {readonly [key: string]: true};

const emptyFreeVariableBindings = emptyObject as FreeVariableBindings;

const freeVariablesUnchanged =
  <a>(_: a) =>
  (context: FreeVariableBindings) =>
    pair(context, emptyFreeVariableBindings);

const freeVariablesMatcher = matchExpression({
  _: freeVariablesUnchanged,
  Function: (val) => (context: FreeVariableBindings) => {
    const nextContext: FreeVariableBindings = {...context, [val.param]: true};
    return pair(nextContext, emptyFreeVariableBindings);
  },
  Variable: Variable.match({
    qualified: freeVariablesUnchanged,
    unqualified: (id: Identifier) => (context: FreeVariableBindings) => {
      if (context[id]) {
        return pair(context, emptyFreeVariableBindings);
      }
      const binding: FreeVariableBindings = {[id]: true};
      return pair(context, binding);
    },
  }),
});

/**
 * Collects all unqualified variables that are undefined (i.e. not bound
 * by an enclosing `EFunction`) in an expression.
 */
export const findFreeVariables = everythingWithContext<FreeVariableBindings>(
  emptyFreeVariableBindings
)<FreeVariableBindings>(unionMerge)(freeVariablesMatcher);

const buildFunction = (body: Expression, param: string) =>
  EFunction({param, body});

/**
 * Helps construct a function expression from an array of parameters and a body.
 */
export const _func = (params: Array<string>, body: Expression): Expression =>
  params.reduceRight(buildFunction, body);

const buildCall = (fun: Expression, arg: Expression) => ECall({fun, arg});

export const _call = (fun: Expression, args: Array<Expression>): Expression =>
  args.reduce(buildCall, fun);

export const toJsonString = (expr: Expression): string =>
  JSON.stringify(expr, null, 2);

/**
 * Sets an expression at the specified path.
 * See `sparseUpdateIn`.
 */
export const sparseSetIn = (
  expr: Expression,
  path: ExpressionPath,
  nextExpr: Expression
) => sparseUpdateIn(expr, path, () => nextExpr);

/**
 * Updates an expression at the specified path using the provided function.
 *
 * If it encounters `undefined` while walking along `path`, it will fill empty
 * `Record`, `Union`, and `List` values.
 *
 * If an `undefined` expression is encountered for any other type, this function
 * will throw.
 */
export const sparseUpdateIn = (
  expr: Expression | undefined,
  path: ExpressionPath,
  update: (arg1: Expression | undefined) => Expression
): Expression => {
  if (path.length < 1) {
    return update(expr);
  }

  const [key, ...nextPath] = path;
  // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
  if (key.in === 'Record') {
    expr = expr || ERecord({});
    return updateRecordHandler(expr)(key, nextPath, update, expr);
  }

  // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
  if (key.in === 'Case') {
    if (!expr)
      throw new Error(`Attempted to update an undefined case expression`);
    return updateCaseHandler(expr)(key, nextPath, update, expr);
  }

  // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
  if (key.in === 'Union') {
    // NOTE: We set `value` to `undefined` here, which is not a valid
    // `Expression`. We assume that our `update` function can handle this case
    // for us.
    // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'. | TS2339 - Property 'tag' does not exist on type '{ in: "Select"; } | { in: "Union"; tag: string; } | { in: "Case"; at: { type: "value"; } | { type: "cases"; val: string; }; } | { in: "List"; index: number; } | { in: "Element"; } | { in: "Call"; at: "arg" | "fun"; } | { ...; } | { ...; } | { ...; }'.
    expr = expr || EUnion({tag: key.tag, value: undefined as any});
    return updateUnionHandler(expr)(nextPath, update, expr);
  }

  // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
  if (key.in === 'List') {
    if (!expr) {
      throw new Error(`Attempted to update an item in list which is undefined`);
    }
    // @ts-expect-error - TS2345 - Argument of type '{ in: "Select"; } | { in: "Union"; tag: string; } | { in: "Case"; at: { type: "value"; } | { type: "cases"; val: string; }; } | { in: "List"; index: number; } | { in: "Element"; } | { in: "Call"; at: "arg" | "fun"; } | { ...; } | { ...; } | { ...; } | undefined' is not assignable to parameter of type '{ in: "List"; index: number; }'.
    return updateListHandler(expr)(key, nextPath, update, expr);
  }

  // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
  if (key.in === 'Element') {
    if (!expr) {
      // We must throw here because we do not have enough info to construct a
      // `Element` - e.g. we do not know the element `type` we need, nor do we
      // want to generate an ID in this otherwise-pure function.
      throw new Error('Expected `Element` but got `undefined`');
    }
    return updateElementHandler(expr)(nextPath, update, expr);
  }

  // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
  if (key.in === 'Call') {
    if (!expr) {
      // We must throw here because we do not have enough info to construct a
      // new `Call` - e.g. we do not know what function we are referencing.
      throw new Error('Expected `Call` but got `undefined`');
    }
    // @ts-expect-error - TS2345 - Argument of type '{ in: "Select"; } | { in: "Union"; tag: string; } | { in: "Case"; at: { type: "value"; } | { type: "cases"; val: string; }; } | { in: "List"; index: number; } | { in: "Element"; } | { in: "Call"; at: "arg" | "fun"; } | { ...; } | { ...; } | { ...; } | undefined' is not assignable to parameter of type '{ at: "arg" | "fun"; in: "Call"; }'.
    return updateCallHandler(expr)(key, nextPath, update, expr);
  }

  // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
  if (key.in === 'Typed') {
    if (!expr) {
      throw new Error('Expected `Typed` but got `undefined`');
    }
    return updateTypedHandler(expr)(nextPath, update, expr);
  }

  // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
  if (key.in === 'Distinct') {
    if (!expr) {
      throw new Error('Expected `Distinct` but got `undefined`');
    }
    return updateDistinctHandler(expr)(nextPath, update, expr);
  }

  // key.in === 'Function' below this line

  if (!expr) {
    // We must throw here because we do not have enough info to construct a new
    // `Function` - e.g. we do not know the type signature of the function.
    throw new Error('Expected `Function` but got `undefined`');
  }

  return updateFunctionHandler(expr)(nextPath, update, expr);
};

const constant3 =
  (a: Expression) =>
  (
    // eslint-disable-next-line unused-imports/no-unused-vars
    b: Array<ExpressionPathPart>,
    // eslint-disable-next-line unused-imports/no-unused-vars
    c: (arg1: Expression | undefined) => Expression,
    // eslint-disable-next-line unused-imports/no-unused-vars
    d: Expression
  ) =>
    a;

const constant4 =
  (a: Expression) =>
  (
    // eslint-disable-next-line unused-imports/no-unused-vars
    b: any,
    // eslint-disable-next-line unused-imports/no-unused-vars
    c: Array<ExpressionPathPart>,
    // eslint-disable-next-line unused-imports/no-unused-vars
    d: (arg1: Expression | undefined) => Expression,
    // eslint-disable-next-line unused-imports/no-unused-vars
    e: Expression
  ) =>
    a;

const updateRecordHandler = matchExpression({
  Record:
    (fields) =>
    (
      key: {
        at: string;
        in: 'Record';
      },
      nextPath: Array<ExpressionPathPart>,
      update: (arg1: Expression | undefined) => Expression,
      expr: Expression
    ) => {
      const {at} = key;
      const oldVal = fields[at];

      const newVal = sparseUpdateIn(oldVal, nextPath, update);

      if (oldVal === newVal) return expr;

      return ERecord({
        ...fields,
        [at]: newVal,
      });
    },
  _: constant4,
});
const updateCaseHandler = matchExpression({
  Case:
    ({value, cases}) =>
    (
      key: {
        at:
          | {
              type: 'value';
            }
          | {
              type: 'cases';
              val: string;
            };
        in: 'Case';
      },
      nextPath: Array<ExpressionPathPart>,
      update: (arg1: Expression | undefined) => Expression,
      expr: Expression
    ) => {
      const {at} = key;

      if (at.type === 'value') {
        const newValue = updateIn(value, nextPath, update);
        if (value === newValue) return expr;
        return ECase({value: newValue, cases});
      }

      const caseLabel = at.val;
      const oldHandler = cases[caseLabel];

      const newHandler = sparseUpdateIn(oldHandler, nextPath, update);

      if (oldHandler === newHandler) return expr;

      return ECase({
        value,
        cases: {
          ...cases,
          [caseLabel]: newHandler,
        },
      });
    },
  _: constant4,
});
const updateUnionHandler = matchExpression({
  Union:
    (union) =>
    (
      nextPath: Array<ExpressionPathPart>,
      update: (arg1: Expression | undefined) => Expression,
      expr: Expression
    ) => {
      const oldVal = union.value;
      const newVal = sparseUpdateIn(oldVal, nextPath, update);

      if (oldVal === newVal) return expr;

      return EUnion({
        tag: union.tag,
        value: newVal,
      });
    },
  _: constant3,
});
const updateListHandler = matchExpression({
  List:
    (oldList) =>
    (
      key: {
        in: 'List';
        index: number;
      },
      nextPath: Array<ExpressionPathPart>,
      update: (arg1: Expression | undefined) => Expression,
      expr: Expression
    ) => {
      const {index} = key;

      if (index > oldList.length - 1) {
        throw new Error(
          `Attempting to update item at index ${key.index} in list with length ${oldList.length}`
        );
      }
      const newList = updateAt(oldList, index, (item) =>
        sparseUpdateIn(item, nextPath, update)
      );

      if (oldList === newList) return expr;

      return EList(newList);
    },
  _: constant4,
});
const updateElementHandler = matchExpression({
  Element:
    (el) =>
    (
      nextPath: Array<ExpressionPathPart>,
      update: (arg1: Expression | undefined) => Expression,
      expr: Expression
    ) => {
      const {data} = el;
      const dataExpr = getElementData(el);
      const newData = sparseUpdateIn(dataExpr, nextPath, update);

      if (data === newData) return expr;

      return EElement(setElementData(el, newData));
    },
  _: constant3,
});
const updateCallHandler = matchExpression({
  Call:
    ({fun, arg}) =>
    (
      key: {
        at: 'fun' | 'arg';
        in: 'Call';
      },
      nextPath: Array<ExpressionPathPart>,
      update: (arg1: Expression | undefined) => Expression,
      expr: Expression
    ) => {
      if (key.at === 'fun') {
        const newFun = sparseUpdateIn(fun, nextPath, update);

        if (fun === newFun) return expr;

        return ECall({
          fun: newFun,
          arg,
        });
      }

      const newArg = sparseUpdateIn(arg, nextPath, update);

      if (arg === newArg) return expr;

      return ECall({
        fun,
        arg: newArg,
      });
    },
  _: constant4,
});
const updateFunctionHandler = matchExpression({
  Function:
    ({param, body}) =>
    (
      nextPath: Array<ExpressionPathPart>,
      update: (arg1: Expression | undefined) => Expression,
      expr: Expression
    ) => {
      const newBody = sparseUpdateIn(body, nextPath, update);

      if (body === newBody) return expr;

      return EFunction({
        param,
        body: newBody,
      });
    },
  _: constant3,
});
const updateTypedHandler = matchExpression({
  Typed:
    (typed) =>
    (
      nextPath: Array<ExpressionPathPart>,
      update: (arg1: Expression | undefined) => Expression,
      expr: Expression
    ) => {
      const newExpr = sparseUpdateIn(typed.expr, nextPath, update);
      if (typed.expr === newExpr) return expr;

      return ETyped({
        type: typed.type,
        expr: newExpr,
      });
    },
  _: constant3,
});
const updateDistinctHandler = matchExpression({
  Distinct:
    ({value, name}) =>
    (
      nextPath: Array<ExpressionPathPart>,
      update: (arg1: Expression | undefined) => Expression,
      expr: Expression
    ) => {
      const newExpr = sparseUpdateIn(value, nextPath, update);
      if (value === newExpr) return expr;

      return EDistinct({
        name,
        value: newExpr,
      });
    },
  _: constant3,
});

/**
 * Delete a field of a `Record` at the given `path`.
 *
 * If the path does not point to an existing `Record`'s field, the original
 * expression will be returned unchanged.
 */
export const deleteInRecord = (
  expr: Expression,
  path: ExpressionPath
): Expression => {
  if (path.length < 1) {
    return expr;
  }
  const lastPart = path[path.length - 1];
  // @ts-expect-error - TS18048 - 'lastPart' is possibly 'undefined'.
  if (lastPart.in !== 'Record') {
    return expr;
  }

  return updateIn(expr, path.slice(0, -1), (nestedExpr) =>
    matchExpression<Expression>({
      Record: (fields) => {
        const newFields = dissoc(lastPart.at)(fields);
        if (newFields === fields) {
          return nestedExpr;
        }
        return ERecord(newFields);
      },
      _: identity,
    })(nestedExpr)
  );
};

/**
 * Recursively calls `deleteInRecord` if the last field
 * deletion leads to a record being empty at the next
 * step of the expression path.
 */
export const recursiveDeleteInRecord = (
  expr: Expression,
  path: ExpressionPath
): Expression => {
  // Check that a value exists at the furthest path
  const exprAtPath = getIn(expr, path);
  if (!exprAtPath) {
    return expr;
  }

  const go = (_expr: Expression, _path: ExpressionPath): Expression => {
    if (_path.length === 0) {
      return _expr;
    }
    const _exprAtPath = getIn(_expr, _path);
    const fieldsAtPath = _exprAtPath ? getRecord(_exprAtPath) : null;

    // If we don't have any fields on a record at path, remove the record
    // here and call the go fn again
    if (fieldsAtPath && Object.keys(fieldsAtPath).length === 0) {
      const _exprAfterDeletion = deleteInRecord(_expr, _path);
      return go(_exprAfterDeletion, _path.slice(0, _path.length - 1));
    }

    // If we either don't have a record or the record still has
    // fields we don't want to do anything to it.
    return _expr;
  };

  // Initially delete the furthest out value that we are targeting
  const exprAfterDeletion = deleteInRecord(expr, path);
  return go(exprAfterDeletion, path.slice(0, path.length - 1));
};

type ExpressionAndExpressionPathPart = Pair<Expression, ExpressionPathPart>;

// used by findPaths variants to determine next expressions and path parts
const getNextExpressionsAndPathParts = matchExpression<
  ExpressionAndExpressionPathPart[]
>({
  Select: ({from}) => [pair(from, {in: 'Select'})],
  Record: (fields) => {
    return Object.keys(fields).reduce<ExpressionAndExpressionPathPart[]>(
      (res, fieldName) => {
        const fieldExpr = fields[fieldName];
        if (fieldExpr) {
          res.push(pair(fieldExpr, {in: 'Record', at: fieldName}));
        }
        return res;
      },
      []
    );
  },
  Union: (union) => [pair(union.value, {in: 'Union', tag: union.tag})],
  Case: ({value, cases}) => {
    return Object.keys(cases).reduce<ExpressionAndExpressionPathPart[]>(
      (res, tag) => {
        const caseExpr = cases[tag];
        if (caseExpr) {
          res.push(pair(caseExpr, {in: 'Case', at: {type: 'cases', val: tag}}));
        }
        return res;
      },
      [pair(value, {in: 'Case', at: {type: 'value'}})]
    );
  },
  List: (list) => list.map((item, index) => pair(item, {in: 'List', index})),
  Element: (el) => [pair(el.data, {in: 'Element'})],
  Call: (call) => [
    pair(call.fun, {in: 'Call', at: 'fun'}),
    pair(call.arg, {in: 'Call', at: 'arg'}),
  ],
  Function: (fn) => [pair(fn.body, {in: 'Function'})],
  Extend: ({record, value}) => [
    pair(record, {in: 'Extend', at: 'record'}),
    pair(value, {in: 'Extend', at: 'value'}),
  ],
  Typed: (typed) => [pair(typed.expr, {in: 'Typed'})],
  Distinct: (distinct) => [pair(distinct.value, {in: 'Distinct'})],
  _: (_) => emptyArray,
});

type ExpressionWithPathStack = {
  expr: Expression;
  path: ExpressionPath;
};

/**
 * Given an `Expression`, provide the first found ExpressionPaths that satisfy the provided predicate.
 */
export const findPath = (
  expr: Expression,
  pred: (arg1: Expression) => boolean
): null | ExpressionPath => {
  const stack: Array<ExpressionWithPathStack> = [{expr, path: []}];
  let path: ExpressionPath | null = null;

  while (stack.length > 0) {
    const exprWithPath = stack.pop();
    if (!exprWithPath) break;

    if (pred(exprWithPath.expr)) {
      path = exprWithPath.path;
      // exit loop whenever we find the first path
      break;
    }

    const nextExpressionsAndPathParts = getNextExpressionsAndPathParts(
      exprWithPath.expr
    );

    // Iterate over `nextExpressionsAndPathParts` in reverse order. This ensures we traverse the ExpressionPath
    // from the last one found in `getNextExpressionsAndPathParts` to the first one found.
    for (let i = nextExpressionsAndPathParts.length - 1; i >= 0; i--) {
      const nextExpressionAndPathPart = nextExpressionsAndPathParts[i];
      if (nextExpressionAndPathPart) {
        const [nextExpr, nextPathPart] = nextExpressionAndPathPart;
        // Create a copy of exprWithPath.path before pushing nextPathPart into it
        const newPath = exprWithPath.path.slice();
        newPath.push(nextPathPart);
        stack.push({
          expr: nextExpr,
          path: newPath,
        });
      }
    }
  }

  return path || null;
};

/**
 * Given a `Expression`, provide a list of all paths within it where expressions
 * where found satisfying the provided predicate.
 *
 * Example:
 *
 * ```js
 * const result = findPaths(
 *   ERecord({
 *     baz: EVariable(Variable.unqualified(identifier('data'))),
 *     foo: EUnion({
 *         tag: 'blah',
 *         value: ERecord({
 *           bar: EVariable(Variable.unqualified(identifier('data')))
 *         }),
 *     }),
 *   }),
 *   expr => expressionsEqual(expr, EVariable(Variable.unqualified(identifier('data')))
 * );
 *
 *
 * // result:
 * //
 * // [
 * //   [
 * //     {in: 'Record', at: 'baz'}
 * //   ],
 * //   [
 * //     {in: 'Record', at: 'foo'},
 * //     {in: 'Union', tag: 'blah'},
 * //     {in: 'Record', at: 'bar'}
 * //   ]
 * // ]
 * ```
 */
export const findPaths = (
  expr: Expression,
  pred: (arg1: Expression) => boolean
): Array<ExpressionPath> => {
  return _findPaths(expr, pred, emptyArray);
};
// We separate this implementation so we don't leak the `path` parameter to the
// public API:
const _findPaths = (
  expr: Expression,
  pred: (arg1: Expression) => boolean,
  path: ExpressionPath
): Array<ExpressionPath> => {
  const result = pred(expr) ? [path] : emptyArray;

  const nextExpressionsAndPathParts = getNextExpressionsAndPathParts(expr);

  if (nextExpressionsAndPathParts.length === 0) {
    return result;
  }

  return result.concat(
    ...nextExpressionsAndPathParts.map((nextExpressionsAndPathPart) => {
      const nextExpr = getFirst(nextExpressionsAndPathPart);
      const nextPathPart = getSecond(nextExpressionsAndPathPart);

      return _findPaths(nextExpr, pred, [...path, nextPathPart]);
    })
  );
};

/**
 * finds all paths in expression that satify predicate with stopping condition
 */
export function findPathsWithStoppable(
  expr: Expression,
  pred: (arg1: Expression) => Stoppable<boolean>
): ExpressionPath[] {
  return _findPathsWithStoppable(expr, pred, emptyArray);
}

function _findPathsWithStoppable(
  expr: Expression,
  pred: (arg1: Expression) => Stoppable<boolean>,
  path: ExpressionPath
): ExpressionPath[] {
  const predOutput = pred(expr);
  const keepPath = getFirst(predOutput);
  const shouldStop = getSecond(predOutput);

  const result = keepPath ? [path] : emptyArray;

  if (shouldStop) {
    return result;
  }

  const nextExpressionsAndPathParts = getNextExpressionsAndPathParts(expr);

  if (nextExpressionsAndPathParts.length === 0) {
    return result;
  }

  return result.concat(
    ...nextExpressionsAndPathParts.map((nextExpressionsAndPathPart) => {
      const nextExpr = getFirst(nextExpressionsAndPathPart);
      const nextPathPart = getSecond(nextExpressionsAndPathPart);

      return _findPathsWithStoppable(nextExpr, pred, [...path, nextPathPart]);
    })
  );
}

export const evaluateWith = <e>(
  envLens: Lens<e, EvalEnv.EvalEnvType>,
  evalElement: (el: ExpressionElement) => (env: e) => unknown
): ((expr: Expression) => (env: e) => unknown) => {
  const evalExpr: (expr: Expression) => (env: e) => any = matchExpression({
    Select:
      ({from, prop}) =>
      (env) => {
        const val = evalExpr(from)(env);
        if (val == null) {
          console.warn(
            `Can‘t look up prop: "${prop}" from: "${
              getVariableExprValue(from) || ''
            }" because it doesn‘t exist on the env.`
          );
          return null;
        }

        if (!isPlainObject(val)) {
          const message = `Can’t access property "${prop}" on a non-record.`;
          if (process.env.NODE_ENV !== 'production') {
            console.error(message);
          } else {
            reportBug('WFDL:SelectOnNonRecord', message, {
              team: 'core',
              priority: 'error',
              ticket: 'https://webflow.atlassian.net/browse/PLAT-1015',
            });
          }
          return null;
        }

        if (!Object.hasOwn(val, prop)) {
          const message = `Can’t access property "${prop}".`;
          if (process.env.NODE_ENV !== 'production') {
            console.error(message);
          } else {
            reportBug('WFDL:SelectUnknownProp', message, {
              team: 'core',
              priority: 'error',
              ticket: 'https://webflow.atlassian.net/browse/PLAT-1066',
            });
          }
          return null;
        }

        return val[prop];
      },
    Variable: (val) => (env) =>
      VariableMap.get(EvalEnv.getValues(envLens.get(env)), val),
    Record: (props) => (env) =>
      mapValues<Expression, any>((prop) => evalExpr(prop)(env))(props),
    Union: (union) => (env) => ({
      tag: union.tag,
      val: evalExpr(union.value)(env),
    }),
    Case:
      ({value, cases}) =>
      (env) => {
        const union = evalExpr(value)(env);
        // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'Expression'.
        const handler = evalExpr(cases[union.tag])(env);
        return handler(union.val);
      },
    List: (list) => (env) => list.map((item) => evalExpr(item)(env)),
    Element: evalElement,
    Call: (call) => (env) => {
      const fun = evalExpr(call.fun)(env);
      const arg = evalExpr(call.arg)(env);
      return fun(arg);
    },
    Function: (fn) => (env) => {
      const {param, body} = fn;
      return (x: any) => {
        const evalEnv = EvalEnv.insertValue(
          envLens.get(env),
          Variable.unqualified(param),
          x
        );
        return evalExpr(body)(envLens.set(evalEnv)(env));
      };
    },
    Distinct:
      ({value}) =>
      (env) =>
        evalExpr(value)(env),
    Literal: (lit) => (env) => {
      const {name, value} = lit;
      const parser = EvalEnv.getParsers(envLens.get(env)).get(name);
      if (!parser) {
        const message = `No parser found for literal value of type ${toHumanReadableString(
          name
        )}.`;
        if (process.env.NODE_ENV !== 'production') {
          console.error(
            message + ' Please contact @sorin on Slack to have this fixed!'
          );
        } else {
          reportBug('WFDL:LiteralParserMissing', message, {
            team: 'core',
            priority: 'error',
            ticket: 'https://webflow.atlassian.net/browse/PLAT-1008',
          });
        }
        return value;
      }
      const result = parser(value);
      return handleParseResult(result)(lit);
    },
    Hole: (_) => {
      throw new Error('Cannot evaluate a hole expression!');
    },
    Boolean: constant,
    Number: constant,
    Text: constant,
    Enum: constant,
    Extend: (extend) => (env) => {
      const {record, prop, value} = extend;
      const evaluatedRecord = evalExpr(record)(env);

      /**
       * Ensure that the Expression we've just evaluated has returned an object
       * with the `prop` we've provided.
       */
      if (!evaluatedRecord || !Object.hasOwn(evaluatedRecord, prop)) {
        throw new Error(
          `Property '${prop}' doesn't exist on provided record and therefore henceforth can't be extended!`
        );
      }

      const evaluatedValue = evalExpr(value)(env);
      return {...evaluatedRecord, [prop]: evaluatedValue};
    },
    Typed: (typed) => evalExpr(typed.expr),
  });

  return evalExpr;
};

const stringElementType = qualify(pluginName('Basic'), componentName('String'));

const evalToNodePojo =
  (el: ExpressionElement) =>
  (env: EvalEnv.EvalEnvType): NodePojo => {
    const {id, type} = el;
    const dataExpr = getElementData(el);
    const data: any = evalExpressionMatcher(dataExpr)(env);
    const isString = namesEqual(type, stringElementType);
    return {
      id,
      type: getName(type),
      pluginType: getPluginName(type),
      data: isString ? {value: data} : removeChildren(data),
      children: data.children || emptyArray,
      text: isString,
    };
  };

const evalExpressionMatcher = evaluateWith<EvalEnv.EvalEnvType>(
  {get: identity, set: constant},
  evalToNodePojo
);

/**
 * Evaluates an Expression.
 *
 * _Primitive Example_
 *
 * ```jsx
 * evaluate({}, EBoolean(true)) // Result true
 * evaluate({}, EBoolean(false)) // Result false
 * evaluate({}, EText("Hello world!")) // Result "Hello World!"
 * evaluate({}, ELiteral({
 *  key: 'This whole value is managed by a custom plugin type!'
 * })) // Result { key: "This whole value is managed by a custom plugin type!"}
 * ```
 *
 * _Override Example_
 *
 * ```jsx
 * const render = EElement({
 *  id: '1',
 *  type: 'DOM',
 *  data: ERecord({
 *    tag: EData(['tag'])
 *  })
 * });
 * const result = evaluate({
 *   // The environment populates the "scope" appropriately.
 *   data: {
 *     tag: 'div'
 *   }
 * }, render);
 * // result === { id: '1', type: 'DOM', data: { tag: 'div' }}
 * ```
 *
 * _Variable Example_
 *
 * ```jsx
 * const result = evaluate({
 *   pluginProvidedNumber: 20
 * }, EVariable('pluginProvidedNumber')
 * // result === 20
 * ```
 *
 * _Function Call Example_
 *
 * ```jsx
 * const expr = ECall({
 *   fun: EVariable('pluginProvidedUpperCase'),
 *   arg: 'Hello world!'
 * })
 *
 * evaluate({
 *   pluginProvidedUpperCase: (str) => str.toUpperCase(),
 * }, expr)
 * // result === "HELLO WORLD!"
 * ```
 */
export const evaluate = (env: EvalEnv.EvalEnvType, expr: Expression): any =>
  evalExpressionMatcher(expr)(env);

const removeChildren = dissoc('children');

const getVariableExprValue = (
  expr: Expression
): undefined | Variable.VariableType =>
  matchExpression<undefined | Variable.VariableType>({
    Variable: identity,
    _: returnUndefined,
  })(expr);

const handleParseResult = Result.match<
  string,
  unknown,
  (arg1: {name: Qualified<TypeName>; value: Json}) => Json | unknown
>({
  Error: constant(({name, value}) => {
    const message = `Failed to parse literal value of type ${toHumanReadableString(
      name
    )}.`;
    if (process.env.NODE_ENV !== 'production') {
      console.error(
        message + ' Please contact @sorin on Slack to have this fixed!'
      );
    } else {
      reportBug('WFDL:LiteralParseFail', message, {
        team: 'core',
        priority: 'error',
        ticket: 'https://webflow.atlassian.net/browse/PLAT-1007',
      });
    }
    return value;
  }),
  Ok: constant,
});
